import { ref, computed } from 'vue';

export type WorkflowStatus = 'pending' | 'processing' | 'completed' | 'error';

export interface WorkflowItem {
    id: string;
    filename: string;
    status: WorkflowStatus;
    progress: number;
    createdAt: string;
    updatedAt: string;
    orderType: 'order' | 'quote';
    error?: string;
    result?: any;
    isNew?: boolean; // Indique si le workflow vient de se terminer (pour notification visuelle)
}

// ⚠️ SINGLETON : workflows partagé entre toutes les instances
const workflows = ref<WorkflowItem[]>([]);
let isInitialized = false;
let refreshInterval: ReturnType<typeof setInterval> | null = null;

export const useWorkflowManager = () => {
    const config = useRuntimeConfig();
    const { ensureValidToken } = useTokenManager();

    // Mapper une Request API vers WorkflowItem
    const mapRequestToWorkflow = (request: any): WorkflowItem => {
        // Mapper le statut de la request vers le statut du workflow
        let workflowStatus: WorkflowStatus = 'pending';
        let progress = 0;

        switch (request.status) {
            case 'RECEIVED':
                workflowStatus = 'pending';
                progress = 10;
                break;
            case 'PROCESSING':
                workflowStatus = 'processing';
                progress = 50;
                break;
            case 'COMPLETED':
                workflowStatus = 'completed';
                progress = 100;
                break;
            case 'FAILED':
                workflowStatus = 'error';
                progress = 0;
                break;
            default:
                workflowStatus = 'pending';
        }

        // Récupérer le filename depuis le premier attachment
        const filename = request.attachments?.[0]?.filename || 'Document';

        // Récupérer l'order_type depuis les metadata
        const orderType = request.metadata?.order_type || 'order';

        return {
            id: request.id,
            filename,
            status: workflowStatus,
            progress,
            createdAt: request.received_at,
            updatedAt: request.updated_at || request.received_at,
            orderType,
            error: request.status === 'FAILED' ? 'Une erreur est survenue' : undefined,
            result: request.result || undefined,
            isNew: false, // Géré par le système de notification
        };
    };

    // Charger les workflows depuis l'API Gateway
    const loadWorkflows = async () => {
        if (!process.client) return;

        try {
            // Récupérer le token JWT
            const token = await ensureValidToken();
            if (!token) {
                console.warn('⚠️ Aucun token JWT disponible pour charger les workflows');
                workflows.value = [];
                return;
            }

            // Appeler l'API Gateway
            const gatewayUrl = config.public.gatewayApiUrl || 'http://localhost:8001';
            const apiKey = useCookie('dj-api-key').value;
            if (!apiKey) {
                throw new Error('API Key non configurée');
            }

            const response = await fetch(`${gatewayUrl}/api/requests?page=1&page_size=50&sort_by=received_at&sort_order=desc`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'dj-api-key': apiKey,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();

            // Mapper les requests vers des WorkflowItems
            const loadedWorkflows = data.items.map(mapRequestToWorkflow);

            // Fusionner avec les workflows locaux pour préserver isNew
            const existingIds = new Set(workflows.value.map((w) => w.id));
            const newWorkflows = loadedWorkflows.map((w: WorkflowItem) => {
                const existing = workflows.value.find((ew) => ew.id === w.id);
                return existing ? { ...w, isNew: existing.isNew } : w;
            });

            workflows.value = newWorkflows;
            console.log('✅ Workflows chargés depuis l\'API:', workflows.value.length);
        } catch (error) {
            console.error('❌ Erreur lors du chargement des workflows depuis l\'API:', error);
            workflows.value = [];
        }
    };

    // Ajouter un nouveau workflow (optimistic update)
    const addWorkflow = (workflow: Omit<WorkflowItem, 'createdAt' | 'updatedAt' | 'progress' | 'status'>) => {
        const newWorkflow: WorkflowItem = {
            ...workflow,
            status: 'pending',
            progress: 0,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        };

        workflows.value.unshift(newWorkflow); // Ajouter au début (optimistic update)
        console.log('✅ Workflow ajouté (optimistic):', newWorkflow.id);
        
        // Recharger depuis l'API pour synchroniser
        setTimeout(() => loadWorkflows(), 2000);
        
        return newWorkflow;
    };

    // Mettre à jour un workflow (optimistic update)
    const updateWorkflow = (id: string, updates: Partial<WorkflowItem>) => {
        const index = workflows.value.findIndex((w) => w.id === id);
        if (index !== -1) {
            workflows.value[index] = {
                ...workflows.value[index],
                ...updates,
                updatedAt: new Date().toISOString(),
            };
            console.log('🔄 Workflow mis à jour (optimistic):', id, updates);
            
            // Si le workflow est terminé (completed ou error), recharger depuis l'API
            if (updates.status === 'completed' || updates.status === 'error') {
                setTimeout(() => loadWorkflows(), 1000);
            }
        }
    };

    // Supprimer un workflow (optimistic update)
    const removeWorkflow = (id: string) => {
        workflows.value = workflows.value.filter((w) => w.id !== id);
        console.log('🗑️ Workflow supprimé (optimistic):', id);
        // Note: La suppression côté serveur devra être implémentée plus tard
    };

    // Récupérer un workflow par ID
    const getWorkflow = (id: string) => {
        return workflows.value.find((w) => w.id === id);
    };

    // Nettoyer les anciens workflows terminés (plus de 24h)
    const cleanOldWorkflows = () => {
        const oneDayAgo = new Date();
        oneDayAgo.setDate(oneDayAgo.getDate() - 1);

        workflows.value = workflows.value.filter((w) => {
            if (w.status === 'completed' || w.status === 'error') {
                const updatedAt = new Date(w.updatedAt);
                return updatedAt > oneDayAgo;
            }
            return true;
        });
        console.log('🧹 Anciens workflows nettoyés');
    };

    // Démarrer le rafraîchissement automatique
    const startAutoRefresh = (intervalMs: number = 30000) => {
        if (refreshInterval) return; // Déjà démarré

        // Charger immédiatement
        loadWorkflows();

        // Puis rafraîchir périodiquement
        refreshInterval = setInterval(() => {
            loadWorkflows();
        }, intervalMs);

        console.log(`🔄 Auto-refresh des workflows démarré (${intervalMs}ms)`);
    };

    // Arrêter le rafraîchissement automatique
    const stopAutoRefresh = () => {
        if (refreshInterval) {
            clearInterval(refreshInterval);
            refreshInterval = null;
            console.log('⏹️ Auto-refresh des workflows arrêté');
        }
    };

    // Computed
    const activeWorkflows = computed(() =>
        workflows.value.filter((w) => w.status === 'pending' || w.status === 'processing'),
    );

    const completedWorkflows = computed(() => workflows.value.filter((w) => w.status === 'completed'));

    const errorWorkflows = computed(() => workflows.value.filter((w) => w.status === 'error'));

    const workflowCount = computed(() => workflows.value.length);

    const hasActiveWorkflows = computed(() => activeWorkflows.value.length > 0);

    // Marquer un workflow comme "vu" (retirer le badge "NOUVEAU")
    const markWorkflowAsSeen = (workflowId: string) => {
        const workflow = workflows.value.find((w) => w.id === workflowId);
        if (workflow && workflow.isNew) {
            workflow.isNew = false;
            console.log(`👀 Workflow marqué comme vu: ${workflowId}`);
        }
    };

    // Initialiser au chargement (une seule fois)
    if (process.client && !isInitialized) {
        // Démarrer l'auto-refresh (toutes les 30 secondes)
        startAutoRefresh(30000);
        cleanOldWorkflows();
        isInitialized = true;
        console.log('✅ WorkflowManager initialisé avec auto-refresh API');
    }

    // Cleanup au unmount
    onBeforeUnmount(() => {
        stopAutoRefresh();
    });

    return {
        workflows,
        activeWorkflows,
        completedWorkflows,
        errorWorkflows,
        workflowCount,
        hasActiveWorkflows,
        addWorkflow,
        updateWorkflow,
        removeWorkflow,
        getWorkflow,
        loadWorkflows,
        cleanOldWorkflows,
        markWorkflowAsSeen,
        startAutoRefresh,
        stopAutoRefresh,
    };
};
